/**
 * Utilitários para verificação de permissões
 */

import {
  hasAppRole,
  hasAppAccess as hasUserAppAccess,
  getAppRolesForUser as getUserAppRoleCodes,
} from '@/lib/usuarioFormUtils';

/**
 * Verifica se o usuário tem permissão para um recurso específico
 * @param {Object} userPermissions - Permissões do usuário
 * @param {string} resource - Recurso (clientes, templates, logins)
 * @param {string} permission - Permissão necessária ('r' para leitura, 'w' para escrita)
 * @returns {boolean} - True se tem permissão, false caso contrário
 */
export function hasPermission(userPermissions, resource, permission = 'r') {
  if (!userPermissions || !userPermissions[resource]) {
    return false
  }
  
  const userPerm = userPermissions[resource]
  
  // Se o usuário tem permissão de escrita, tem acesso total
  if (userPerm === 'w') {
    return true
  }
  
  // Se precisa de leitura e o usuário tem leitura
  if (permission === 'r' && userPerm === 'r') {
    return true
  }
  
  // Se precisa de escrita e o usuário tem escrita
  if (permission === 'w' && userPerm === 'w') {
    return true
  }
  
  return false
}

/**
 * Verifica se o usuário pode acessar clientes
 * @param {Object} userPermissions - Permissões do usuário
 * @returns {boolean}
 */
export function canAccessClientes(userPermissions) {
  return hasPermission(userPermissions, 'clientes', 'r')
}

/**
 * Verifica se o usuário pode acessar templates
 * @param {Object} userPermissions - Permissões do usuário
 * @returns {boolean}
 */
export function canAccessTemplates(userPermissions) {
  return hasPermission(userPermissions, 'templates', 'r')
}

/**
 * Verifica se o usuário pode acessar gestão de usuários
 * @param {Object} userPermissions - Permissões do usuário
 * @returns {boolean}
 */
export function canAccessUsuarios(userPermissions) {
  return hasPermission(userPermissions, 'logins', 'r')
}

export function hasLoginsWrite(userPermissions) {
  return userPermissions?.logins === 'w';
}

export function canAccessSupervisao(userPermissions, supervisionados = []) {
  if (hasLoginsWrite(userPermissions)) {
    return true;
  }
  return Array.isArray(supervisionados) && supervisionados.length > 0;
}

export function canSuperviseUser(userPermissions, supervisionados, userId) {
  if (hasLoginsWrite(userPermissions)) {
    return true;
  }
  if (!userId || !Array.isArray(supervisionados)) {
    return false;
  }
  return supervisionados.some((item) => item.id === userId);
}

export function hasAppPermission(userPermissions, appId, role) {
  return hasAppRole(userPermissions?.apps, appId, role);
}

export function hasAppAccessPermission(userPermissions, appId) {
  return hasUserAppAccess(userPermissions?.apps, appId);
}

export function getAppRoles(userPermissions, appId) {
  return getUserAppRoleCodes(userPermissions?.apps, appId);
}

/**
 * Obtém as permissões do usuário do localStorage ou store
 * @returns {Object|null} - Permissões do usuário ou null se não autenticado
 */
export function getUserPermissions() {
  try {
    const loginData = localStorage.getItem('qrsconfig@login')
    if (loginData) {
      // Se estiver usando apenas localStorage para autenticação
      return null // Será obtido do store
    }
    return null
  } catch (error) {
    console.error('Erro ao obter permissões do usuário:', error)
    return null
  }
} 